import React, { useState, useEffect } from 'react';

import DatePicker from '../../components/DatePicker/DatePicker';
import NumberInput from 'Components/Form/Input/NumberInput/NumberInput';
import ErrorMessage from '../AccommodationSearch/components/ErrorMessage';

const CaravanParkReservation = () => {
  const seasons = drupalSettings.caravan_park_seasons;
  const today = new Date();
  const todayFormatted = formatDate(today);
  today.setUTCHours(0, 0, 0, 0);

  const [isLoading, setIsLoading] = useState(true);
  const [cartLimitReached, setCartLimitReached] = useState(false);

  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();

  const [people, setPeople] = useState(1);
  const [isMember, setIsMember] = useState(false);

  const [dateSelectionType, setDateSelectionType] = useState(
    drupalSettings.show_seasonal_tab_by_default ? 'season' : 'day'
  );

  const [selectedSeason, setSelectedSeason] = useState('default');
  const [dayAvailabilities, setDayAvailabilities] = useState();
  const [availableDates, setAvailableDates] = useState();
  const [minimalDates, setMinimalDates] = useState([]);

  const [seasonAvailabilities, setSeasonAvailabilities] = useState();
  const [sites, setSites] = useState([]);
  // Site is a room in Hlx. In day booking, it's selected randomly.
  // In season booking the user can select it. "Empty" value should be 'default'.
  const [selectedSite, setSelectedSite] = useState('default');
  const [storageOnlyBooking, setStorageOnlyBooking] = useState(false);

  const [vehicleNumber, setVehicleNumber] = useState();
  const [memberNumber, setMemberNumber] = useState();

  const [price, setPrice] = useState();
  const [product, setProduct] = useState();
  const [addingtoCart, setAddingToCart] = useState(false);

  const [redirect, setRedirect] = useState();

  const [errorMessage, setErrorMessage] = useState();
  const [availabilityErrorMessage, setAvailabilityErrorMessage] = useState(
    drupalSettings.custom_not_available_message
  );

  const [extraServices, setExtraServices] = useState(
    drupalSettings.accommodation_extra_services[dateSelectionType] ?? []
  );
  const [selectedExtras, setSelectedExtras] = useState([]);

  // Check if deadline for same day booking has passed.
  let isBookingDeadline = false;
  const bookingDeadlineValues = drupalSettings.booking_deadline.split(':');
  if (bookingDeadlineValues.length > 1) {
    const bookingDeadline = new Date().setHours(
      bookingDeadlineValues[0],
      bookingDeadlineValues[1]
    );

    if (new Date().getTime() > bookingDeadline) {
      isBookingDeadline = true;
    }
  }

  const calendarInfo = (
    <>
      <hr />
      <div className="datepicker-footer">
        <div className="datepicker-footer__date datepicker-footer__date--available">
          <span className="datepicker-footer__icon datepicker-footer__icon--available-icon"></span>
          {Drupal.t('Available date')}
        </div>
        <div className="datepicker-footer__date datepicker-footer__date--unavailable">
          <span className="datepicker-footer__icon datepicker-footer__icon--unavailable-icon"></span>
          {Drupal.t('Booked')}
        </div>
        <div className="datepicker-footer__date datepicker-footer__date--limited">
          <span className="datepicker-footer__icon datepicker-footer__icon--limited-icon"></span>
          {Drupal.t('Limited availability')}
        </div>
      </div>
    </>
  );

  // Checks if we can add more items to the cart.
  async function checkCart() {
    const response = await fetch(Drupal.url(`cart?_format=json`), {
      credentials: 'include',
    });
    const cartData = await response.json();
    let reservationsInCart = 0;

    if (!cartData || !cartData[0]) {
      return true;
    }

    cartData[0].order_items.forEach((orderItem) => {
      // Counting only reservations towards the limit.
      if (orderItem.purchased_entity.type == 'extra_service') {
        return;
      }
      reservationsInCart++;
    });

    if (reservationsInCart >= drupalSettings.cart_max_items) {
      setCartLimitReached(true);
      setErrorMessage(drupalSettings.cart_limit_message);

      return false;
    }
    return true;
  }

  useEffect(() => {
    async function getRoomAvailabilities() {
      // If there are no seasons configured, booking can't be done.
      if (!seasons) {
        setErrorMessage(Drupal.t("Bookings can't be made at the moment."));
        return;
      }

      // First checking if cart isn't full.
      const cartOk = await checkCart();
      if (!cartOk) {
        return;
      }

      const searchStart = new Date();
      const searchStartFormatted = searchStart.toISOString().split('T')[0];

      const searchEnd = seasons[seasons.length - 1].endDate;
      const url = new URL('/api/roomavailabilities', process.env.IPS_API_URL);
      url.searchParams.set('resort', drupalSettings.active_site);
      url.searchParams.set('startDate', searchStartFormatted);
      url.searchParams.set('endDate', searchEnd);

      try {
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();

          const dayAvailabilityData = data.result
            .map((accommodation) => {
              if (
                accommodation.roomTypeId ===
                parseInt(drupalSettings.hotellinx_ids.day)
              ) {
                return accommodation;
              }
            })
            .filter((accommodation) => accommodation !== undefined);

          setDayAvailabilities(dayAvailabilityData);

          // List of dates with availability for each day.
          const availabilityPerDay = {};
          dayAvailabilityData.forEach((dayAvailability) => {
            dayAvailability.availability.forEach((day) => {
              const dateString = day.date.split('T')[0];
              if (day.isAvailable) {
                availabilityPerDay[dateString] = availabilityPerDay[dateString]
                  ? availabilityPerDay[dateString] + 1
                  : 1;
              } else {
                if (!availabilityPerDay[dateString]) {
                  availabilityPerDay[dateString] = 0;
                }
              }
            });
          });
          setAvailableDates(availabilityPerDay);

          const minimal = Object.keys(availabilityPerDay)
            .map((day) => {
              if (
                availabilityPerDay[day] !== 0 &&
                availabilityPerDay[day] <= drupalSettings.hotellinx_buffers.day
              ) {
                return day;
              }
            })
            .filter((date) => date !== undefined);
          setMinimalDates(minimal);

          const seasonAvailabilityData = data.result
            .map((accommodation) => {
              if (
                accommodation.roomTypeId ===
                parseInt(drupalSettings.hotellinx_ids.season)
              ) {
                return accommodation;
              }
            })
            .filter((accommodation) => accommodation !== undefined);
          setSeasonAvailabilities(seasonAvailabilityData);
        } else {
          setAvailabilityErrorMessage(
            Drupal.t(
              'There was an error fetching availabilities. Please refresh the page and try again.'
            )
          );
        }
      } catch (error) {
        setAvailabilityErrorMessage(
          Drupal.t(
            'There was an error fetching availabilities. Please refresh the page and try again.'
          )
        );
        console.log(error);
      }

      setIsLoading(false);
    }

    getRoomAvailabilities();
  }, []);

  // Find available sites for the season.
  useEffect(() => {
    setErrorMessage(null);

    if (!selectedSeason || selectedSeason === 'default') {
      return;
    }

    const seasonData = getSelectedSeasonData();
    const availableSites = getAvailableSites(
      seasonData.startDate,
      seasonData.endDate,
      seasonAvailabilities
    );

    if (availableSites.length > drupalSettings.hotellinx_buffers.season) {
      setSites(availableSites);
    } else {
      setErrorMessage(drupalSettings.caravan_park_limited_availability_message);
      setSites([]);
    }
  }, [selectedSeason]);

  // Pick an available site for the selected period.
  useEffect(() => {
    async function fetchAvailability() {
      setIsLoading(true);

      let availableSites = getAvailableSites(
        startDate,
        endDate,
        dayAvailabilities
      );

      let site = null;
      // Randomize the order of rooms. Then check the room's
      // availability, until one available room is found, and set it
      // as the selected room.
      availableSites.sort(() => Math.random() - 0.5);
      for (const availableSite of availableSites) {
        const isAvailable = await checkRoomAvailability(availableSite.roomId);
        if (isAvailable) {
          site = availableSite.roomId;
          break;
        }
      }
      if (site) {
        setSelectedSite(site.toString());
      } else {
        setErrorMessage(
          Drupal.t(
            'No sites available for the selected date. Please select another date and try again.'
          )
        );
      }
      setIsLoading(false);
    }

    setSelectedSite('default');
    setPrice(null);
    setErrorMessage(null);
    if (startDate && endDate && dayAvailabilities) {
      if (includesMinimalDates(startDate, endDate)) {
        setErrorMessage(
          drupalSettings.caravan_park_limited_availability_message
        );
        return;
      }

      if (
        dailyBookingIsWithinOneSeason(
          new Date(startDate).setHours(15, 0, 0),
          new Date(endDate).setHours(15, 0, 0)
        )
      ) {
        fetchAvailability();
      } else {
        setErrorMessage(drupalSettings.caravan_cross_season_message);
      }
    }
  }, [startDate, endDate, dayAvailabilities]);

  // Check that the daily reservation booking fits within only one season.
  function dailyBookingIsWithinOneSeason(bookingStart, bookingEnd) {
    return seasons.some((season) => {
      const seasonStart = new Date(season.startDate).setHours(0, 0, 0);
      const seasonEnd = new Date(season.endDate).setHours(23, 59, 59);

      if (
        bookingStart >= seasonStart &&
        bookingStart <= seasonEnd &&
        bookingEnd >= seasonStart &&
        bookingEnd <= seasonEnd
      ) {
        return true;
      }
    });
  }

  /**
   * Check if the stay includes any dates
   * with minimal booking availability.
   */
  function includesMinimalDates(bookingStart, bookingEnd) {
    let currentDate = new Date(bookingStart);
    let currentDateFormatted = formatDate(currentDate);
    const end = formatDate(new Date(bookingEnd));

    while (currentDateFormatted !== end) {
      if (minimalDates.includes(currentDateFormatted)) {
        return true;
      }

      currentDate.setDate(currentDate.getDate() + 1);
      currentDateFormatted = formatDate(currentDate);
    }

    return false;
  }

  function getAvailableSites(start, end, data) {
    // Seasonal reservations may have their start date in the past,
    // but since it doesn't make sense to check availability for past
    // dates, setting the start date to current date.
    let searchStartDate = new Date(start);
    if (today.getTime() > new Date(start).getTime()) {
      searchStartDate = new Date();
    }

    const availableSites = data
      .map((room) => {
        const startIndex = room.availability.findIndex(
          (availability) =>
            new Date(availability.date).toDateString() ===
            searchStartDate.toDateString()
        );
        const endIndex = room.availability.findIndex(
          (availability) =>
            new Date(availability.date).toDateString() ===
            new Date(end).toDateString()
        );

        for (let i = startIndex; i < endIndex; i++) {
          if (!room.availability[i].isAvailable) {
            return;
          }
        }

        const { roomId, roomNumber, roomTypeId } = room;
        return { roomId, roomNumber, roomTypeId };
      })
      .filter((room) => room && room !== undefined && room.roomNumber)
      .sort((a, b) => a.roomNumber - b.roomNumber);

    return availableSites;
  }

  function selectSeason(e) {
    setSelectedSite('default');
    setSites([]);
    setStartDate(null);
    setEndDate(null);
    setPrice(null);
    setSelectedSeason(e.target.value);
    setStorageOnlyBooking(false);
  }

  useEffect(() => {
    if (startDate) {
      setSelectedSeason('default');
    }
  }, [startDate]);

  function formatDate(date) {
    const offset = date.getTimezoneOffset();
    const formattedDate = new Date(date.getTime() - offset * 60 * 1000);
    return formattedDate.toISOString().split('T')[0];
  }

  async function checkRoomAvailability(roomId) {
    let searchStartDate;
    let searchEndDate;

    if (dateSelectionType === 'day') {
      searchStartDate = formatDate(startDate);
      searchEndDate = formatDate(endDate);
    } else {
      const seasonData = getSelectedSeasonData();
      if (new Date(seasonData.startDate) > today) {
        searchStartDate = seasonData.startDate;
      } else {
        searchStartDate = today.toISOString().split('T')[0];
      }
      searchEndDate = seasonData.endDate;
    }

    const url = new URL('/api/singleroomavailability', process.env.IPS_API_URL);
    url.searchParams.set('startDate', searchStartDate);
    url.searchParams.set('endDate', searchEndDate);
    url.searchParams.set('roomId', roomId);

    try {
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();

        data.result.forEach((day) => {
          if (!day.isAvailable) {
            return false;
          }
        });

        return true;
      }
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    async function getPrice() {
      setPrice(null);
      setIsLoading(true);

      let id;
      let searchStartDate;
      let searchEndDate;
      let campaignCode = null;
      // We make the assumption that either there can be a SF discount (because it's for daily reservations)
      // or a storage only option (because it's for seasonal reservations)
      if (isMember) {
        campaignCode = drupalSettings.sf_member_code;
      } else if (storageOnlyBooking) {
        campaignCode = drupalSettings.caravan_park_storage_only_booking_code;
      }

      if (dateSelectionType === 'day') {
        searchStartDate = formatDate(startDate);
        searchEndDate = formatDate(endDate);
        id = drupalSettings.hotellinx_ids.day;
      } else {
        const seasonData = getSelectedSeasonData();
        if (new Date(seasonData.startDate) > today) {
          searchStartDate = seasonData.startDate;
        } else {
          searchStartDate = today.toISOString().split('T')[0];
        }
        searchEndDate = seasonData.endDate;
        id = drupalSettings.hotellinx_ids.season;
      }

      const url = new URL('/api/accommodations', process.env.IPS_API_URL);
      url.searchParams.set('resort', drupalSettings.active_site);
      url.searchParams.set('roomTypeIds', id);
      url.searchParams.set('adults', 1);
      url.searchParams.set('startDate', searchStartDate);
      url.searchParams.set('endDate', searchEndDate);
      if (campaignCode) {
        url.searchParams.set('campaignCode', campaignCode);
      }

      try {
        const response = await fetch(url);
        const data = await response.json();

        if (response.ok) {
          data.result.sort((a, b) => a.price - b.price);
          if (data.result[0].price) {
            setProduct(data.result[0]);
          }
        } else {
          switch (response.status) {
            case 453:
              setErrorMessage(
                Drupal.t(
                  'No availability for this period. Please consider a different room type.'
                )
              );
              break;
            case 455: {
              // Reservation length exceeds the maximum.
              setErrorMessage(drupalSettings.reservation_length_error_message);
              break;
            }
            case 456:
            case 457: {
              // Reservation contains days that can't be booked online.
              setErrorMessage(
                drupalSettings.caravan_park_limited_availability_message
              );
              break;
            }

            default:
              setErrorMessage(data.error);
          }
        }
      } catch (error) {
        console.log(error);
        setErrorMessage(
          Drupal.t(
            'There was an error checking availability. Please refresh the page and try again'
          )
        );
      }
      setIsLoading(false);
    }

    async function checkSeasonRoomAvailability() {
      const roomIsAvailable = await checkRoomAvailability(selectedSite);
      if (roomIsAvailable) {
        getPrice();
      } else {
        setErrorMessage(
          Drupal.t(
            "The selected site isn't available for booking. Please select another site."
          )
        );
      }
    }

    if (selectedSite !== 'default') {
      if (dateSelectionType === 'day') {
        getPrice();
      } else {
        checkSeasonRoomAvailability();
      }
    }
  }, [selectedSite, isMember, storageOnlyBooking]);

  async function addToCart(event) {
    event.preventDefault();
    setErrorMessage(null);
    setAddingToCart(true);

    const cartOk = await checkCart();
    if (!cartOk) {
      setAddingToCart(false);
      return;
    }

    if (!product) {
      return;
    }

    if (!vehicleNumber) {
      setErrorMessage(Drupal.t('Please set the vehicle registration number.'));
      setAddingToCart(false);
      return;
    }

    if (isMember && !memberNumber) {
      setErrorMessage(Drupal.t('Please set your SF-Caravan ry membership ID.'));
      setAddingToCart(false);
      return;
    }

    if (isMember && memberNumber.length < 4) {
      setErrorMessage(
        Drupal.t('The membership number should be at least 4 digits long.')
      );
      setAddingToCart(false);
      return;
    }

    let campaignCode = null;
    // We make the assumption that either there can be a SF discount (because it's for daily reservations)
    // or a storage only option (because it's for seasonal reservations)
    if (isMember) {
      campaignCode = drupalSettings.sf_member_code;
    } else if (storageOnlyBooking) {
      campaignCode = drupalSettings.caravan_park_storage_only_booking_code;
    }

    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },

      body: JSON.stringify([
        {
          purchased_entity_type: 'commerce_product_variation',
          purchased_entity_id: product.variationId,
          quantity: '1',
          combine: false, // False will create another order item, while true will increment an existing item.
          campaignData: null,
          caravanParkData: {
            memberNumber,
            vehicleNumber,
            people,
            campaignCode,
            storageOnlyBooking,
          },
        },
      ]),
    };

    let searchStartDate;
    let searchEndDate;
    if (dateSelectionType === 'day') {
      searchStartDate = formatDate(startDate);
      searchEndDate = formatDate(endDate);
    } else {
      const seasonData = getSelectedSeasonData();
      if (new Date(seasonData.startDate) > today) {
        searchStartDate = seasonData.startDate;
      } else {
        searchStartDate = today.toISOString().split('T')[0];
      }
      searchEndDate = seasonData.endDate;
    }

    const response = await fetch(
      `${drupalSettings.base_url}/${drupalSettings.path.currentLanguage}/cart/
			add?_format=json
			&adults=1&startDate=${searchStartDate}&endDate=${searchEndDate}
			&price=${product.price}
			&rateplanCode=${product.rateplanCode}
			&children=0&infants=0&pets=false
			&extraServices=${selectedExtras ? selectedExtras.toString() : ''}
			&roomId=${selectedSite}`,
      requestOptions
    );

    if (response.ok) {
      setRedirect(`/${drupalSettings.path.currentLanguage}/cart`);
    } else {
      setAddingToCart(false);
      setErrorMessage(
        Drupal.t(
          'Error adding product to the cart. Please refresh the page and try again.'
        )
      );
    }
  }

  useEffect(() => {
    if (redirect) {
      window.location.replace(redirect);
    }
  }, [redirect]);

  function submitButtonText() {
    if (isLoading) {
      return Drupal.t('Loading');
    }

    let text = Drupal.t('Reserve site');
    if (price) {
      text += ` ${new Intl.NumberFormat('fi-FI', {
        style: 'currency',
        currency: 'EUR',
      }).format(parseFloat(price))}`;
    }

    return text;
  }

  function disableSubmit() {
    if (selectedSite === 'default' || isLoading || addingtoCart || !price) {
      return true;
    }

    return false;
  }

  function dayIsDisabled(date) {
    if (availableDates) {
      const dateString = formatDate(date);

      // If the deadline has passed for today.
      if (isBookingDeadline && dateString === todayFormatted) {
        return true;
      }

      if (availableDates[dateString] === 0) {
        // User should be able to select a blocked date as a departure date,
        // if the previous day wasn't blocked.
        if (!startDate || startDate.getTime() > date.getTime()) {
          return true;
        }

        let yesterday = new Date(date);
        yesterday = new Date(yesterday.setUTCDate(date.getUTCDate() - 1));
        if (availableDates[formatDate(yesterday)] === 0) {
          return true;
        }
      }
    }

    return false;
  }

  function checkAvailability(date) {
    if (availableDates) {
      const dateString = formatDate(date);
      if (!availableDates[dateString]) {
        return 'react-calendar__month-view__days__day--blocked';
      } else if (minimalDates.includes(dateString)) {
        return 'react-calendar__month-view__days__day--limited';
      }
    }
    return '';
  }

  function getSelectedSeasonData() {
    return seasons.find(
      (season) =>
        season.name[drupalSettings.path.currentLanguage] === selectedSeason
    );
  }

  // Reset date and season data and price when tab is changed.
  function onTabChange(tab) {
    setDateSelectionType(tab);

    setSelectedExtras([]);
    setExtraServices(drupalSettings.accommodation_extra_services[tab] ?? []);
    setStartDate(null);
    setEndDate(null);
    setSelectedSite('default');
    setPrice(null);
    setSelectedSeason('default');
    setIsMember(false);
    setMemberNumber(null);
    setStorageOnlyBooking(false);
  }

  function onExtraServiceChange(e) {
    if (e.target.checked) {
      setSelectedExtras([...selectedExtras, e.target.value]);
    } else {
      const newExtras = selectedExtras.filter(
        (extra) => extra !== e.target.value
      );
      setSelectedExtras(newExtras);
    }
  }

  // Calculate the total price for the reservation.
  useEffect(() => {
    if (product) {
      let totalPrice = product.price;
      selectedExtras.forEach((extra) => {
        totalPrice += parseFloat(
          extraServices.find((extraService) => extraService.id === extra)
            .price ?? 0
        );
      });

      setPrice(totalPrice);
    }
  }, [selectedExtras, product]);

  return (
    <div
      className="accommodation-reservation-form caravan-park-reservation-form"
      style={{ minHeight: '20rem' }}
    >
      <form>
        <h3>
          1. {Drupal.t('Date', {}, { context: 'caravanparkwidget' })}
          <span style={{ color: '#ff1706' }}>*</span>
        </h3>

        <div className="caravan-park-reservation-form--tabs">
          <h4
            className={dateSelectionType === 'day' ? 'selected' : ''}
            onClick={() => onTabChange('day')}
          >
            {Drupal.t('Day or week')}
          </h4>
          <h4
            className={dateSelectionType === 'season' ? 'selected' : ''}
            onClick={() => onTabChange('season')}
          >
            {Drupal.t('Seasonal spots')}
          </h4>
        </div>

        {dateSelectionType === 'day' && (
          <>
            <strong>{Drupal.t('Select dates')}</strong>
            <DatePicker
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              startDate={startDate}
              endDate={endDate}
              parseDay={checkAvailability}
              dayIsDisabled={dayIsDisabled}
              availableDates={availableDates}
              content={calendarInfo}
              disabled={
                isLoading ||
                !dayAvailabilities ||
                !dayAvailabilities.length === 0
              }
            />

            {availabilityErrorMessage && (
              <ErrorMessage message={availabilityErrorMessage} />
            )}
          </>
        )}

        {dateSelectionType === 'season' && (
          <>
            <strong>{Drupal.t('Summer and winter spots')}</strong>
            <div className="form-item__dropdown">
              <select
                value={selectedSeason ?? 'default'}
                onChange={selectSeason}
                name="select-season"
                id="select-season"
                className="form-item__select"
                disabled={isLoading || !seasonAvailabilities}
              >
                <option value="default">
                  {isLoading ? Drupal.t('Ladataan') : Drupal.t('Select')}
                </option>
                {seasons
                  .filter((season) => !season.disableSeasonBooking)
                  .map((season, i) => (
                    <option
                      key={i}
                      value={season.name[drupalSettings.path.currentLanguage]}
                    >
                      {season.name[drupalSettings.path.currentLanguage]}
                    </option>
                  ))}
              </select>
            </div>

            {selectedSeason !== 'default' &&
              !!getSelectedSeasonData()?.storageOnlyBooking && (
                <label
                  htmlFor="storage-only"
                  className="form-item__label form-item__label--checkbox"
                >
                  <input
                    id="storage-only"
                    name="storage-only"
                    type="checkbox"
                    className="visually-hidden"
                    checked={storageOnlyBooking}
                    onChange={(e) => setStorageOnlyBooking(e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  {Drupal.t('Book only for storage')}
                </label>
              )}
          </>
        )}

        <h3>2. {Drupal.t('Booking information')}</h3>
        <strong>
          {Drupal.t('Number of people')}
          <span style={{ color: '#ff1706' }}>*</span>
        </strong>
        <p>
          {Drupal.t(
            'For security reasons, please state the number of people in the party'
          )}
        </p>
        <NumberInput
          id="people"
          class="number-input--adults"
          min={1}
          max={100}
          value={people}
          setter={setPeople}
        />

        <strong>
          {Drupal.t('Vehicle registration number')}
          <span style={{ color: '#ff1706' }}>*</span>
        </strong>
        <input
          className="form-item__textfield"
          type="text"
          name="vehicle"
          id="vehicle"
          onChange={(e) => setVehicleNumber(e.target.value)}
          required={true}
        />

        {dateSelectionType === 'season' && (
          <>
            <strong>{Drupal.t('Caravan site')}</strong>
            <p>{Drupal.t('The available spots for the chosen date')}</p>
            <a
              className="caravana-park-map-link"
              href={drupalSettings.caravan_park_map}
              target="_blank"
              rel="noreferrer"
              style={{ marginTop: '10px', display: 'block' }}
            >
              <strong>{Drupal.t('Check the caravan park map')}</strong>
            </a>
            <div className="form-item__dropdown">
              <select
                onChange={(e) => setSelectedSite(e.target.value)}
                name="select-site"
                id="select-site"
                className="form-item__select"
                disabled={selectedSeason === 'default' || isLoading}
                value={selectedSite ?? 'default'}
              >
                <option value="default">{Drupal.t('Select')}</option>
                {sites.map((site) => (
                  <option value={site.roomId} key={site.roomId}>
                    {site.roomNumber}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        {dateSelectionType === 'day' && (
          <>
            <label
              htmlFor="member"
              className="form-item__label form-item__label--checkbox"
            >
              <input
                id="member"
                name="member"
                type="checkbox"
                className="visually-hidden"
                checked={isMember}
                onChange={(e) => setIsMember(e.target.checked)}
              />
              <span className="checkmark"></span>
              {Drupal.t('I am SF-Caravan ry member')}
            </label>

            {isMember && (
              <input
                name="member-number"
                id="member-number"
                className="form-item__textfield"
                type="text"
                required={true}
                onChange={(e) => setMemberNumber(e.target.value)}
              />
            )}
          </>
        )}

        {extraServices && extraServices.length > 0 && (
          <>
            <h4>{Drupal.t('Extra services')}</h4>

            <div className="accommodation-reservation-form__extra-services">
              {extraServices.map((extraService) => (
                <label
                  className="form-item__label form-item__label--checkbox"
                  key={extraService.id}
                >
                  <input
                    name={`service-${extraService.id}`}
                    id={`service-${extraService.id}`}
                    type="checkbox"
                    className="visually-hidden"
                    onChange={onExtraServiceChange}
                    value={extraService.id}
                  />
                  <span className="checkmark"></span>
                  {extraService.name} (+{extraService.price}€)
                </label>
              ))}
            </div>
          </>
        )}

        {errorMessage && <ErrorMessage message={errorMessage} />}

        <button
          onClick={(e) => addToCart(e)}
          className={`button button--large-full-width ${
            disableSubmit() ? 'button--disabled' : ''
          }`}
          disabled={disableSubmit()}
        >
          {submitButtonText()}
        </button>
      </form>
    </div>
  );
};

export default CaravanParkReservation;
